// Pure utility functions extracted from Helpers.js mixin
// The original mixin is untouched — this provides typed alternatives for new/migrated code
// Stable order within a date group: newest timestamp first, then merchant
// name A→Z as the tiebreak. Without this, transactions sharing a timestamp
// (or with no time component) can re-order between refetches.
export function sortTransactionGroup(g) {
    return [...g].sort((a, b) => {
        const ta = a.timestamp ? new Date(a.timestamp).getTime() : 0;
        const tb = b.timestamp ? new Date(b.timestamp).getTime() : 0;
        if (ta !== tb)
            return tb - ta;
        const na = (a.merchant ? a.merchant.name : a.merchantName) ?? '';
        const nb = (b.merchant ? b.merchant.name : b.merchantName) ?? '';
        return na.localeCompare(nb, undefined, { sensitivity: 'base' });
    });
}
const SHORT_MONTH_NAMES = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
];
export function currentTaxYearLabel() {
    // UK tax year runs April–March. Jan–Mar belongs to the prior April's tax year.
    const now = new Date();
    const y = now.getFullYear();
    const startYear = now.getMonth() >= 3 ? y : y - 1;
    const endYear = startYear + 1;
    return `Tax ${String(startYear).slice(-2)}-${String(endYear).slice(-2)}`;
}
export function formatDayMonth(d) {
    return `${d.getUTCDate()} ${SHORT_MONTH_NAMES[d.getUTCMonth()]}`;
}
export function formatResponseRange(fromIso, toIso) {
    // API returns timezone-less ISO strings (e.g. "2026-04-24T00:00:00") that JS
    // otherwise parses as local time; appending 'Z' keeps them aligned with the
    // UTC accessors used in formatDayMonth so BST doesn't shift the day back.
    const toUtc = (iso) => new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
    const from = toUtc(fromIso);
    const to = toUtc(toIso);
    const fromStr = formatDayMonth(from);
    const toStr = formatDayMonth(to);
    const year = to.getUTCFullYear();
    if (from.getUTCFullYear() !== year) {
        return `${fromStr} ${from.getUTCFullYear()} - ${toStr} ${year}`;
    }
    return `${fromStr} - ${toStr} ${year}`;
}
export function formatMonthYearShort(monthKey) {
    // monthKey is "YYYY-MM" (1-indexed, e.g. "2025-08") → "Aug 2025"
    if (!monthKey)
        return '';
    const [year, month] = monthKey.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1, 1);
    return `${date.toLocaleString('default', { month: 'short' })} ${year}`;
}
export function formatDateTime(dateString, humanificate = true) {
    const date = new Date(dateString);
    if (humanificate) {
        const now = new Date();
        const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const nowOnly = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const diffTime = nowOnly.getTime() - dateOnly.getTime();
        const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
        const options = { hour: '2-digit', minute: '2-digit' };
        if (diffDays === 0) {
            return "Today at " + date.toLocaleTimeString([], options);
        }
        else if (diffDays === 1) {
            return "Yesterday at " + date.toLocaleTimeString([], options);
        }
        else if (diffDays <= 7) {
            return `${diffDays} days ago at ` + date.toLocaleTimeString([], options);
        }
    }
    return date.toLocaleString([], { hour: '2-digit', minute: '2-digit', year: 'numeric', month: 'numeric', day: 'numeric' });
}
export function stringToDate(dateString) {
    if (!dateString || dateString === 'NaN/NaN/NaN') {
        return null;
    }
    const [day, month, year] = dateString.split('/');
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
export function dateToString(dateString) {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${day}/${month}/${year}`;
}
export function removeNullProperties(obj) {
    Object.keys(obj).forEach(key => obj[key] == null && delete obj[key]);
    return obj;
}
// True when a background colour is light enough to need dark text on it.
// Accepts hex (#rrggbb) or rgb(...) strings.
export function isLightBg(color) {
    let r = 0, g = 0, b = 0;
    if (color?.startsWith('rgb')) {
        const m = color.match(/\d+/g);
        if (m)
            [r, g, b] = m.map(Number);
    }
    else if (color?.startsWith('#') && color.length === 7) {
        r = parseInt(color.slice(1, 3), 16);
        g = parseInt(color.slice(3, 5), 16);
        b = parseInt(color.slice(5, 7), 16);
    }
    return (r * 299 + g * 587 + b * 114) / 1000 > 186;
}
export function processMonetaryAmounts(text) {
    if (localStorage.getItem('randomiseFigures') !== 'true') {
        return text;
    }
    const monetaryPattern = /(?:£|\$)(?:\d{1,3}(?:,\d{3})*(?:\.\d{2})?|\d+(?:\.\d{2})?)|(?:\d+(?:\.\d{2})?\s*(?:pounds?|pence|p|GBP|USD))/gi;
    return text.replace(monetaryPattern, (match) => {
        if (match.startsWith('£') || match.startsWith('$')) {
            return match.charAt(0) + '####';
        }
        else {
            return '####' + match.replace(/^\d+(?:\.\d{2})?/, '');
        }
    });
}
export function preventNegative(event) {
    if (event.key === '-' || event.key === 'e') {
        event.preventDefault();
    }
}
export function formattedTransactionDate(createdDateTime) {
    const date = new Date(createdDateTime);
    if (isNaN(date.getTime())) {
        return 'Invalid Date';
    }
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month}, ${year}`;
}
// Bucket transactions by calendar day, preserving order. Key prefers an
// already-formatted `timestampString`, else the date portion of `timestamp`.
export function groupByDay(txs) {
    const buckets = new Map();
    for (const t of txs) {
        const key = t.timestampString ?? (t.timestamp ?? '').slice(0, 10);
        const arr = buckets.get(key) ?? [];
        arr.push(t);
        buckets.set(key, arr);
    }
    return Array.from(buckets.values());
}
const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
// e.g. "Tuesday, 2 Jun 26". Returns '' for empty/invalid input.
export function formatTimestamp(ts) {
    if (!ts)
        return '';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime()))
        return '';
    const yy = String(d.getFullYear()).slice(-2);
    return `${dayNames[d.getDay()]}, ${d.getDate()} ${monthNames[d.getMonth()]} ${yy}`;
}
export function cleanBrandName(name) {
    if (!name)
        return '';
    const map = { 'ø': 'o', 'Ø': 'O', 'æ': 'ae', 'Æ': 'AE', 'ß': 'ss', 'ð': 'd', 'Ð': 'D', 'þ': 'th', 'Þ': 'Th', 'đ': 'd', 'Đ': 'D', 'ł': 'l', 'Ł': 'L' };
    let ret = name.replace(/[ /]/g, '+');
    ret = ret.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^\x20-\x7E]/g, function (c) { return map[c] || ''; });
    ret = encodeURIComponent(ret);
    return ret;
}
export function getBrandLogoUrl(name) {
    if (!name)
        return require('@/assets/round3.png');
    return 'https://api.sysodev.link/BrandLogos/' + cleanBrandName(name);
}
export function getCategoryIconUrl(iconPath) {
    if (!iconPath)
        return require('@/assets/round3.png');
    const base = iconPath.toLowerCase().replace('.svg', '');
    const file = base + '.png';
    try {
        return require('@/assets/transactions/' + file);
    }
    catch (e) {
        return require('@/assets/round3.png');
    }
}
// localStorage override wins; otherwise fall back to the app-wide global
// (typically read from `getCurrentInstance().appContext.config.globalProperties.$wellKnownLogosOnly`).
export function isWellKnownLogosOnly(globalFallback) {
    const ls = localStorage.getItem('wellKnownLogosOnly');
    return ls !== null ? ls === 'true' : !!globalFallback;
}
// Same 3-step rule the SpendingAnalysis merchants tab uses:
// 1. wellKnownLogosOnly + !wellKnown → category icon on a coloured circle.
// 2. Otherwise → brand logo.
// 3. On error → category icon; if that also fails → round3.
export function getMerchantLogoUrl(m) {
    if (m.wellKnownLogosOnly && !m.wellKnown)
        return getCategoryIconUrl(m.iconPath);
    return getBrandLogoUrl(m.name);
}
// The category PNGs are white-on-transparent, so when we're using the category
// icon as the avatar we need to place it on a coloured circle.
export function getMerchantLogoStyle(m) {
    const useCategory = m.wellKnownLogosOnly && !m.wellKnown;
    if (!useCategory || !m.iconColour)
        return {};
    // Skip the coloured circle when the icon resolves to the generic round3
    // placeholder — that PNG isn't a category icon and the ring looks wrong.
    const round3 = require('@/assets/round3.png');
    if (getCategoryIconUrl(m.iconPath) === round3)
        return {};
    return { backgroundColor: m.iconColour, padding: '4px' };
}
export function onMerchantLogoError(e, m) {
    const img = e.target;
    const fallback = getCategoryIconUrl(m.iconPath);
    // Avoid a loop if the category icon itself failed — drop straight to round3.
    if (img.src.endsWith(fallback)) {
        img.src = require('@/assets/round3.png');
    }
    else {
        img.src = fallback;
    }
}
